import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useAuthStore, isPlatformOwner } from '../../stores/authStore'
import { Shield, Eye, EyeOff, AlertTriangle } from 'lucide-react'
import { getTenantSlugFromHost, storeTenantSlug } from '../../lib/tenant'
import PublicLayout from '../../components/layouts/PublicLayout'

const loginSchema = z.object({
  tenant: z.string().min(1, 'Organization slug is required'),
  email: z.string().email('Valid email is required'),
  password: z.string().min(1, 'Password is required'),
})

type LoginFormData = z.infer<typeof loginSchema>

export default function LoginPage() {
  const navigate = useNavigate()
  const login = useAuthStore((state) => state.login)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [failedAttempts, setFailedAttempts] = useState(0)
  const tenantFromEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta.env.VITE_TENANT_SLUG as string | undefined) || null
      : null
  const tenantFromHost =
    typeof window !== 'undefined' ? getTenantSlugFromHost(window.location.hostname) : null
  const initialTenant = tenantFromEnv ?? tenantFromHost ?? ''

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      tenant: initialTenant,
    },
  })

  const onSubmit = async (data: LoginFormData) => {
    if (failedAttempts >= 5) {
      setLoginError('Account temporarily locked. Please try again in 15 minutes or contact support.')
      return
    }

    setIsSubmitting(true)
    setLoginError(null)

    try {
      storeTenantSlug(data.tenant.trim())
      await login(data.email, data.password)

      // Get user role from store after login
      const user = useAuthStore.getState().user

      // Navigate based on role/ownership
      if (user?.role === 'parent') {
        navigate('/app/my-child')
      } else {
        navigate('/app/dashboard')
      }
    } catch (error: unknown) {
      setFailedAttempts((prev) => prev + 1)

      const errorMessage = error instanceof Error ? error.message : 'Unknown error'

      if (errorMessage.includes('locked') || failedAttempts >= 4) {
        setLoginError('Account temporarily locked due to multiple failed attempts. Please try again in 15 minutes.')
      } else {
        setLoginError('Invalid email or password. Please try again.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <PublicLayout>
      <div className="min-h-[calc(100vh-4rem)] bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <Shield className="h-12 w-12 text-primary-600" />
        </Link>
        <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
          Sign in to Grace Alone ABA
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          HIPAA-compliant ABA practice management
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* Security Notice */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Secure Connection</p>
                <p className="mt-1">
                  This connection is encrypted. Your login attempt will be logged for security.
                </p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {loginError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{loginError}</p>
                </div>
              </div>
            )}

            <div>
              <label htmlFor="tenant" className="block text-sm font-medium text-gray-700">
                Organization slug
              </label>
              <div className="mt-1">
                <input
                  id="tenant"
                  type="text"
                  autoComplete="organization"
                  {...register('tenant')}
                  className="input"
                  disabled={failedAttempts >= 5 || Boolean(tenantFromHost)}
                />
                {errors.tenant && (
                  <p className="mt-1 text-sm text-red-600">{errors.tenant.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  {...register('email')}
                  className="input"
                  placeholder="you@yourpractice.com"
                  disabled={failedAttempts >= 5}
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  {...register('password')}
                  className="input pr-10"
                  disabled={failedAttempts >= 5}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/contact" className="font-medium text-primary-600 hover:text-primary-500">
                  Password help
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || failedAttempts >= 5}
                className="btn-primary w-full py-3"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>

          {/* Session Security Info */}
          <div className="mt-6 border-t border-gray-200 pt-6">
            <div className="text-xs text-gray-500 space-y-1">
              <p>- Sessions expire after 1 hour of inactivity</p>
              <p>- All access is logged for HIPAA compliance</p>
              <p>- Account locks after 5 failed attempts</p>
            </div>
          </div>
        </div>

        <p className="mt-4 text-center text-sm text-gray-600">
          Need access?{' '}
          <Link to="/contact" className="font-medium text-primary-600 hover:text-primary-500">
            Contact your administrator
          </Link>
        </p>
      </div>
      </div>
    </PublicLayout>
  )
}

